import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
  Tooltip,
  CircularProgress,
  TablePagination,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonOff as OfflinePlayerIcon,
  GroupAdd as BulkAddIcon,
  HelpOutline as HelpIcon,
  Merge as MergeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { offlinePlayersApi, mergeRequestsApi } from '../api';
import type { OfflinePlayer, MergeRequest } from '../types';
import { IconPicker, renderIcon, BulkImportPlayersDialog, GlobalMergeConfirmDialog } from '../components';
import { OfflinePlayersEducationModal } from '../components/education';
import { hasSeenEducationModal } from '../lib/educationModals';
import { useDocumentTitle, usePagination } from '../hooks/core';

/**
 * Calculate name similarity between two strings.
 * Returns a value between 0 and 1, where 1 is an exact match.
 * Uses character frequency overlap normalized by the longer string.
 */
function calculateNameSimilarity(name1: string, name2: string): number {
  const n1 = name1.toLowerCase().trim();
  const n2 = name2.toLowerCase().trim();

  if (n1 === n2) return 1.0;
  if (!n1 || !n2) return 0.0;

  // Count character occurrences (excluding spaces)
  const chars1 = new Map<string, number>();
  const chars2 = new Map<string, number>();

  for (const c of n1) {
    if (c !== ' ') chars1.set(c, (chars1.get(c) || 0) + 1);
  }
  for (const c of n2) {
    if (c !== ' ') chars2.set(c, (chars2.get(c) || 0) + 1);
  }

  // Calculate overlap
  let overlap = 0;
  for (const [c, count1] of chars1) {
    const count2 = chars2.get(c) || 0;
    overlap += Math.min(count1, count2);
  }

  // Calculate lengths excluding spaces
  const len1 = [...n1].filter(c => c !== ' ').length;
  const len2 = [...n2].filter(c => c !== ' ').length;
  const maxLen = Math.max(len1, len2);

  if (maxLen === 0) return 0.0;
  return overlap / maxLen;
}

const SIMILARITY_THRESHOLD = 0.9; // 90% match required

export const OfflinePlayersPage: React.FC = () => {
  useDocumentTitle('Offline Players');
  const [searchParams, setSearchParams] = useSearchParams();

  // Merge suggestion from notification
  const mergeTargetId = searchParams.get('mergeTarget');
  const mergeTargetName = searchParams.get('mergeTargetName');

  const [players, setPlayers] = useState<OfflinePlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { paginationProps, resetPage, getPaginatedItems } = usePagination();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<OfflinePlayer | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [iconName, setIconName] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [educationModalOpen, setEducationModalOpen] = useState(() => !hasSeenEducationModal('offline_players'));

  // Merge suggestion state
  const [mergeSuccess, setMergeSuccess] = useState<string | null>(null);
  const [pendingMergeRequests, setPendingMergeRequests] = useState<MergeRequest[]>([]);
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [selectedPlayerForMerge, setSelectedPlayerForMerge] = useState<OfflinePlayer | null>(null);

  const fetchPlayers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await offlinePlayersApi.getMyOfflinePlayers();
      setPlayers(data);
      setError(null);
    } catch (_err) {
      setError('Failed to load offline players');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch pending merge requests created by the current user
  const fetchPendingRequests = useCallback(async () => {
    try {
      const requests = await mergeRequestsApi.getMyOutgoingPending();
      setPendingMergeRequests(requests);
    } catch {
      // Silently fail - pending requests are not critical
      setPendingMergeRequests([]);
    }
  }, []);

  useEffect(() => {
    fetchPlayers();
    fetchPendingRequests();
  }, [fetchPlayers, fetchPendingRequests]);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Filter players by search query
  const filteredPlayers = useMemo(() => {
    if (!searchQuery.trim()) return players;
    const query = searchQuery.toLowerCase().trim();
    return players.filter(p =>
      p.displayName.toLowerCase().includes(query) ||
      (p.handle && p.handle.toLowerCase().includes(query))
    );
  }, [players, searchQuery]);

  // Calculate potential matches for merge suggestion
  const potentialMatches = useMemo(() => {
    if (!mergeTargetName || !players.length) return new Map<string, number>();
    const matches = new Map<string, number>();
    for (const player of players) {
      const similarity = calculateNameSimilarity(player.displayName, mergeTargetName);
      if (similarity >= SIMILARITY_THRESHOLD) {
        matches.set(player.id, similarity);
      }
    }
    return matches;
  }, [mergeTargetName, players]);

  // Map of pending merge requests by offline player ID
  const pendingByPlayer = useMemo(() => {
    const map = new Map<string, MergeRequest>();
    for (const req of pendingMergeRequests) {
      map.set(req.offlinePlayerId, req);
    }
    return map;
  }, [pendingMergeRequests]);

  // Paginated players
  const paginatedPlayers = getPaginatedItems(filteredPlayers);

  // Handle search change (reset to first page)
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    resetPage();
  };

  const handleOpenDialog = (player?: OfflinePlayer) => {
    if (player) {
      setEditingPlayer(player);
      setDisplayName(player.displayName);
      setHandle(player.handle || '');
      setIconName(player.iconName || '');
    } else {
      setEditingPlayer(null);
      setDisplayName('');
      setHandle('');
      setIconName('');
    }
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlayer(null);
    setDisplayName('');
    setHandle('');
    setIconName('');
    setDialogError(null);
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      setDialogError('Display name is required');
      return;
    }

    setSaving(true);
    setDialogError(null);

    try {
      if (editingPlayer) {
        await offlinePlayersApi.updateMyOfflinePlayer(editingPlayer.id, {
          displayName: displayName.trim(),
          handle: handle.trim() || undefined,
          iconName: iconName.trim() || undefined,
        });
      } else {
        await offlinePlayersApi.createMyOfflinePlayer({
          displayName: displayName.trim(),
          handle: handle.trim() || undefined,
          iconName: iconName.trim() || undefined,
        });
      }
      handleCloseDialog();
      fetchPlayers();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to save offline player');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (player: OfflinePlayer) => {
    if (!confirm(`Delete "${player.displayName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await offlinePlayersApi.deleteMyOfflinePlayer(player.id);
      fetchPlayers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete offline player');
    }
  };

  // Dismiss merge suggestion banner
  const handleDismissMergeSuggestion = () => {
    setSearchParams({}, { replace: true });
  };

  // Open confirmation dialog for merge request
  const handleInitiateMerge = (player: OfflinePlayer) => {
    if (!mergeTargetId) return;
    setSelectedPlayerForMerge(player);
    setMergeConfirmOpen(true);
  };

  // Handle successful merge creation from dialog
  const handleMergeCreated = (result: { requests: unknown[] }) => {
    const boardCount = result.requests.length;
    setMergeSuccess(
      `Merge request sent! ${mergeTargetName} will need to approve the merge across ${boardCount} board${boardCount !== 1 ? 's' : ''}.`
    );
    setMergeConfirmOpen(false);
    setSelectedPlayerForMerge(null);
    // Refresh both players and pending requests
    fetchPlayers();
    fetchPendingRequests();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <OfflinePlayerIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Offline Players
            </Typography>
            <Tooltip title="Learn more about offline players">
              <IconButton
                component={RouterLink}
                to="/support/boards#offline-players"
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage players who don't have their own account. You can add them to any of your boards.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Bulk Import">
            <IconButton
              color="primary"
              onClick={() => setBulkImportOpen(true)}
              sx={{
                bgcolor: 'grey.200',
                '&:hover': {
                  bgcolor: 'grey.300',
                },
              }}
            >
              <BulkAddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Player">
            <IconButton
              color="primary"
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
              data-testid="add-offline-player-button"
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mergeSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMergeSuccess(null)}>
          {mergeSuccess}
        </Alert>
      )}

      {/* Merge suggestion banner */}
      {mergeTargetId && mergeTargetName && potentialMatches.size > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          icon={<MergeIcon />}
          action={
            <IconButton
              aria-label="dismiss"
              color="inherit"
              size="small"
              onClick={handleDismissMergeSuggestion}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          <Typography variant="subtitle2" gutterBottom>
            Merge Suggestion for {mergeTargetName}
          </Typography>
          <Typography variant="body2">
            We found {potentialMatches.size} offline player{potentialMatches.size !== 1 ? 's' : ''} with similar names.
            Click the <MergeIcon sx={{ fontSize: 16, verticalAlign: 'middle', mx: 0.5 }} /> button to merge them into {mergeTargetName}'s account.
          </Typography>
        </Alert>
      )}

      {/* Merge suggestion banner - no matches */}
      {mergeTargetId && mergeTargetName && potentialMatches.size === 0 && !loading && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleDismissMergeSuggestion}>
              Dismiss
            </Button>
          }
        >
          No offline players found matching "{mergeTargetName}". They may have already been merged or the names don't match closely enough.
        </Alert>
      )}

      {players.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <OfflinePlayerIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No offline players yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create offline players to represent friends or family who don't have their own account.
            You can then add them to boards and manage their squares.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Your First Offline Player
          </Button>
        </Paper>
      ) : (
        <>
          {/* Search field */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {filteredPlayers.length} offline player{filteredPlayers.length !== 1 ? 's' : ''}
              {searchQuery.trim() && ` (filtered from ${players.length})`}
            </Typography>
            <TextField
              size="small"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ ml: 'auto', minWidth: 180 }}
              InputProps={{
                sx: { height: 32 },
              }}
              inputProps={{ 'data-testid': 'offline-player-search-input' }}
            />
          </Box>

          {/* Players table */}
          {filteredPlayers.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No players found matching "{searchQuery}"
              </Typography>
            </Paper>
          ) : (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Player</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 80 }}>Boards</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 80 }}>Squares</th>
                      <th style={{ textAlign: 'right', padding: '12px', fontWeight: 600, width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPlayers.map((player) => {
                      const hasSquares = (player.totalSquares ?? 0) > 0;
                      const isPotentialMatch = potentialMatches.has(player.id);
                      const similarity = potentialMatches.get(player.id);
                      const pendingRequest = pendingByPlayer.get(player.id);
                      const hasPending = !!pendingRequest;
                      return (
                      <tr
                        key={player.id}
                        style={{
                          borderBottom: '1px solid #e0e0e0',
                          backgroundColor: hasPending ? '#e3f2fd' : isPotentialMatch ? '#fff8e1' : '#fff',
                        }}
                      >
                        {/* Player Name */}
                        <td style={{ padding: '12px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              variant="rounded"
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: hasPending ? 'info.main' : isPotentialMatch ? 'warning.main' : 'grey.600',
                                borderRadius: 1,
                              }}
                            >
                              {player.iconName ? renderIcon(player.iconName, 20) : getInitials(player.displayName)}
                            </Avatar>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {player.displayName}
                                </Typography>
                                {hasPending && (
                                  <Chip
                                    icon={<MergeIcon sx={{ fontSize: 14 }} />}
                                    label={`Pending → ${pendingRequest.targetUser?.displayName || 'Unknown'}`}
                                    size="small"
                                    color="info"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                  />
                                )}
                                {!hasPending && isPotentialMatch && (
                                  <Chip
                                    label={`${Math.round((similarity || 0) * 100)}% match`}
                                    size="small"
                                    color="warning"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                              {player.handle && (
                                <Typography variant="caption" color="text.secondary">
                                  {player.handle}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </td>

                        {/* Boards */}
                        <td style={{ textAlign: 'center', padding: '12px' }}>
                          <Typography variant="body2" color="text.secondary">
                            {player.boardCount ?? 0}
                          </Typography>
                        </td>

                        {/* Squares */}
                        <td style={{ textAlign: 'center', padding: '12px' }}>
                          <Typography variant="body2" color="text.secondary">
                            {player.totalSquares ?? 0}
                          </Typography>
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: 'right', padding: '12px' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            {!hasPending && isPotentialMatch && (
                              <Tooltip title={`Merge into ${mergeTargetName}'s account`}>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleInitiateMerge(player)}
                                >
                                  <MergeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenDialog(player)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={hasSquares ? 'Remove squares first to delete' : 'Delete'}>
                              <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(player)}
                                disabled={hasSquares}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
              <TablePagination
                component="div"
                count={filteredPlayers.length}
                {...paginationProps}
              />
            </>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingPlayer ? 'Edit Offline Player' : 'Create Offline Player'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Display Name"
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            sx={{ mb: 2 }}
            required
            inputProps={{ 'data-testid': 'offline-player-name-input' }}
          />
          <TextField
            margin="dense"
            label="Handle (optional)"
            fullWidth
            value={handle}
            onChange={(e) => setHandle(e.target.value.slice(0, 4))}
            helperText="1-4 characters shown on the board grid"
            inputProps={{ maxLength: 4, 'data-testid': 'offline-player-handle-input' }}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Icon (optional)
          </Typography>
          <IconPicker
            value={iconName}
            onChange={setIconName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving} data-testid="offline-player-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !displayName.trim()}
            data-testid="offline-player-save-button"
          >
            {saving ? 'Saving...' : editingPlayer ? 'Save Changes' : 'Create Player'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Dialog */}
      <BulkImportPlayersDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onComplete={() => {
          setBulkImportOpen(false);
          fetchPlayers();
        }}
      />

      <OfflinePlayersEducationModal
        open={educationModalOpen}
        onClose={() => setEducationModalOpen(false)}
      />

      {/* Global Merge Confirmation Dialog */}
      <GlobalMergeConfirmDialog
        open={mergeConfirmOpen}
        offlinePlayer={selectedPlayerForMerge}
        targetUserId={mergeTargetId || ''}
        targetUserName={mergeTargetName || ''}
        onClose={() => {
          setMergeConfirmOpen(false);
          setSelectedPlayerForMerge(null);
        }}
        onMergeCreated={handleMergeCreated}
      />
    </Box>
  );
};
